'use client'

import ConfirmModal from '@/components/confirm-modal'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import {
    addClientNote,
    Attachment,
    createClient,
    CreateClientData,
    deleteClientAttachment,
    deleteClientNote,
    getClient,
    Note,
    updateClient,
    UpdateClientData,
    uploadClientAttachment,
} from '@/lib/api/clients/client-api'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { Role } from '@/lib/api/common/interfaces/role.interface'
import {
    fetchPostalCode,
    PostalCodeException,
} from '@/lib/api/fetch-postal-code'
import { getRoles } from '@/lib/api/roles/roles-api'
import { isCpf } from '@/lib/form-state/common-validators'
import {
    createSetting,
    getSettingByName,
    updateSetting,
} from '@/lib/api/utils/settings-api'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { handleDateInputPaste } from '@/lib/utils/date-input'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdAttachFile,
    MdDelete,
    MdExpandLess,
    MdExpandMore,
    MdHandshake,
    MdNote,
    MdSave,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { formatPhoneNumber } from '@/lib/form-state/util'

interface FormData {
    name: string
    cpf: string
    email: string
    whatsAppNumber: string
    birthDate: string
    gender: string
    street: string
    number: string
    complement: string
    neighborhood: string
    city: string
    state: string
    zipCode: string
    country: string
    password: string
    roleId: string
    partnerId: number | null
    professionalId: number | null
}

interface MetadataItem {
    key: string
    value: string
    expanded: boolean
}

interface MetadataField {
    key: string
    isTextarea: boolean
}

export default function ClientFormPage() {
    const { user, isPartner, isEmployee } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id
    const { clientLabel, ClientLabel } = useClientLabel()
    const hidePartnerSelector = isPartner || (isEmployee && !!user.partnerId)
    const isHealthSegment = user?.company?.segment === 'HEALTH'
    const hideProfessionalSelector = !isHealthSegment
    // CPF só é obrigatório na saúde; nos demais segmentos é opcional, mas, se
    // preenchido, continua validado.
    const isCpfRequired = isHealthSegment

    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)
    const [loadingCep, setLoadingCep] = useState(false)
    const [activeTab, setActiveTab] = useState<
        'basic' | 'metadata' | 'attachments' | 'notes'
    >('basic')
    const [metadata, setMetadata] = useState<MetadataItem[]>([])
    const [attachments, setAttachments] = useState<Attachment[]>([])
    const [notes, setNotes] = useState<Note[]>([])
    const [uploading, setUploading] = useState(false)
    const [noteText, setNoteText] = useState('')
    const [addingNote, setAddingNote] = useState(false)
    const [roles, setRoles] = useState<Role[]>([])
    const [loadingRoles, setLoadingRoles] = useState(false)
    const [showRemoveAttachmentModal, setShowRemoveAttachmentModal] =
        useState(false)
    const [attachmentIndexToRemove, setAttachmentIndexToRemove] = useState<
        number | null
    >(null)
    const [showRemoveNoteModal, setShowRemoveNoteModal] = useState(false)
    const [noteIndexToRemove, setNoteIndexToRemove] = useState<number | null>(
        null
    )
    const [uploadForm, setUploadForm] = useState({
        file: null as File | null,
        description: '',
        details: '',
    })

    const [formData, setFormData] = useState<FormData>({
        name: '',
        cpf: '',
        email: '',
        whatsAppNumber: '',
        birthDate: '',
        gender: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'BR',
        password: '',
        roleId: '',
        partnerId: hidePartnerSelector ? user.partnerId || null : null,
        professionalId: null,
    })

    useEffect(() => {
        if (isEditing && id) {
            loadClient()
        } else {
            loadMetadataTemplate()
        }
    }, [id, isEditing, user?.companyId])

    useEffect(() => {
        if (!isEditing) {
            loadRoles()
        }
    }, [isEditing])

    const loadRoles = async () => {
        setLoadingRoles(true)
        try {
            const response = await getRoles()
            if (response.success && response.result) {
                setRoles(response.result)
                const defaultClientRole = response.result.find(
                    (r: Role) => r.defaultForType === 'CLIENT'
                )
                if (defaultClientRole) {
                    setFormData(prev => ({
                        ...prev,
                        roleId: String(defaultClientRole.id),
                    }))
                }
            }
        } catch (error) {
            console.error('Erro ao carregar perfis de acesso:', error)
        } finally {
            setLoadingRoles(false)
        }
    }

    const loadClient = async () => {
        if (!id) return

        setLoadingData(true)
        try {
            const response = await getClient(parseInt(id, 10))
            if (response.success && response.result) {
                const client = response.result
                setFormData({
                    name: client.name || '',
                    cpf: client.cpf || '',
                    email: client.email || '',
                    whatsAppNumber: client.whatsAppNumber
                        ? formatPhoneNumber(client.whatsAppNumber)
                        : '',
                    birthDate: client.birthDate
                        ? client.birthDate.substring(0, 10)
                        : '',
                    gender: client.gender || '',
                    street: client.street || '',
                    number: client.number || '',
                    complement: client.complement || '',
                    neighborhood: client.neighborhood || '',
                    city: client.city || '',
                    state: client.state || '',
                    zipCode: client.zipCode || '',
                    country: client.country || 'BR',
                    password: '',
                    roleId: '',
                    partnerId: client.partnerId || null,
                    professionalId: client.professionalId || null,
                })

                if (client.metadata && Array.isArray(client.metadata)) {
                    setMetadata(
                        client.metadata.map((item: any) => ({
                            key: item.key || '',
                            value: item.value || '',
                            expanded: false,
                        }))
                    )
                } else {
                    await loadMetadataTemplate()
                }

                if (client.attachments && Array.isArray(client.attachments)) {
                    setAttachments(client.attachments as Attachment[])
                } else {
                    setAttachments([])
                }

                if (client.notes && Array.isArray(client.notes)) {
                    setNotes(client.notes as Note[])
                } else {
                    setNotes([])
                }
            } else {
                toast.error(response.error || `Erro ao carregar ${clientLabel}`)
                navigate('/person/clients')
            }
        } catch (error) {
            toast.error(`Erro ao carregar ${clientLabel}`)
            navigate('/person/clients')
        } finally {
            setLoadingData(false)
        }
    }

    const formatZipCode = (value: string): string => {
        const numbers = value.replace(/\D/g, '')
        if (numbers.length <= 5) {
            return numbers
        }
        return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`
    }

    const handleZipCodeChange = async (value: string) => {
        const formatted = formatZipCode(value)
        updateFormField('zipCode', formatted)

        const cleanCode = formatted.replace(/\D/g, '')
        if (cleanCode.length === 8) {
            setLoadingCep(true)
            try {
                const postalData = await fetchPostalCode(cleanCode)
                setFormData(prev => ({
                    ...prev,
                    zipCode: formatted,
                    street: postalData.logradouro || prev.street,
                    neighborhood: postalData.bairro || prev.neighborhood,
                    city: postalData.localidade || prev.city,
                    state: postalData.uf || prev.state,
                }))
            } catch (error) {
                // Limpa os campos preenchidos via CEP para não manter dados inconsistentes.
                setFormData(prev => ({
                    ...prev,
                    street: '',
                    neighborhood: '',
                    city: '',
                    state: '',
                }))
                if (error instanceof PostalCodeException) {
                    toast.error(error.message || 'CEP não encontrado')
                } else {
                    toast.error('Erro ao buscar CEP')
                }
            } finally {
                setLoadingCep(false)
            }
        }
    }

    const updateFormField = (field: keyof FormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const addMetadataItem = () => {
        setMetadata(prev => [...prev, { key: '', value: '', expanded: false }])
    }

    const removeMetadataItem = (index: number) => {
        setMetadata(prev => prev.filter((_, i) => i !== index))
    }

    const updateMetadataItem = (
        index: number,
        field: 'key' | 'value',
        value: string
    ) => {
        setMetadata(prev =>
            prev.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        )
    }

    const toggleMetadataExpanded = (index: number) => {
        setMetadata(prev =>
            prev.map((item, i) =>
                i === index ? { ...item, expanded: !item.expanded } : item
            )
        )
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setUploadForm(prev => ({ ...prev, file: e.target.files![0] }))
        }
    }

    const handleUploadAttachment = async () => {
        if (!id || !isEditing) {
            toast.error('Salve o cliente primeiro antes de adicionar anexos')
            return
        }

        if (!uploadForm.file) {
            toast.error('Selecione um arquivo')
            return
        }

        if (!uploadForm.description.trim()) {
            toast.error('Descrição é obrigatória')
            return
        }

        if (!user?.name) {
            toast.error('Usuário não autenticado')
            return
        }

        setUploading(true)
        try {
            const response = await uploadClientAttachment(
                parseInt(id, 10),
                uploadForm.file,
                user.name,
                uploadForm.description.trim(),
                uploadForm.details.trim() || undefined
            )

            if (response.success && response.result) {
                if (response.result.attachments) {
                    setAttachments(response.result.attachments)
                }
                toast.success('Anexo adicionado com sucesso!')
                setUploadForm({
                    file: null,
                    description: '',
                    details: '',
                })
                // Reset file input
                const fileInput = document.getElementById(
                    'attachment-file'
                ) as HTMLInputElement
                if (fileInput) {
                    fileInput.value = ''
                }
            } else {
                toast.error('Erro ao adicionar anexo')
            }
        } catch (error: any) {
            console.error('Erro ao fazer upload:', error)
            toast.error(
                error?.response?.data?.message || 'Erro ao adicionar anexo'
            )
        } finally {
            setUploading(false)
        }
    }

    const handleRemoveAttachment = (index: number) => {
        if (!id || !isEditing) {
            return
        }
        setAttachmentIndexToRemove(index)
        setShowRemoveAttachmentModal(true)
    }

    const confirmRemoveAttachment = async () => {
        if (!id || !isEditing || attachmentIndexToRemove === null) return

        try {
            const response = await deleteClientAttachment(
                parseInt(id, 10),
                attachmentIndexToRemove
            )

            if (response.success) {
                setAttachments(prev =>
                    prev.filter((_, i) => i !== attachmentIndexToRemove)
                )
                toast.success('Anexo removido com sucesso!')
            } else {
                toast.error('Erro ao remover anexo')
            }
        } catch (error: any) {
            console.error('Erro ao remover anexo:', error)
            toast.error('Erro ao remover anexo')
        } finally {
            setShowRemoveAttachmentModal(false)
            setAttachmentIndexToRemove(null)
        }
    }

    const handleAddNote = async () => {
        if (!id || !isEditing) {
            toast.error('Salve o cliente primeiro antes de adicionar anotações')
            return
        }

        if (!noteText.trim()) {
            toast.error('Digite uma anotação')
            return
        }

        if (!user?.name) {
            toast.error('Usuário não autenticado')
            return
        }

        setAddingNote(true)
        try {
            const response = await addClientNote(
                parseInt(id, 10),
                noteText.trim(),
                user.name
            )

            if (response.success && response.result) {
                if (response.result.notes) {
                    setNotes(response.result.notes)
                }
                toast.success('Anotação adicionada com sucesso!')
                setNoteText('')
            } else {
                toast.error('Erro ao adicionar anotação')
            }
        } catch (error: any) {
            console.error('Erro ao adicionar anotação:', error)
            toast.error(
                error?.response?.data?.message || 'Erro ao adicionar anotação'
            )
        } finally {
            setAddingNote(false)
        }
    }

    const handleRemoveNote = (index: number) => {
        if (!id || !isEditing) {
            return
        }
        setNoteIndexToRemove(index)
        setShowRemoveNoteModal(true)
    }

    const confirmRemoveNote = async () => {
        if (!id || !isEditing || noteIndexToRemove === null) return

        try {
            const response = await deleteClientNote(
                parseInt(id, 10),
                noteIndexToRemove
            )

            if (response.success) {
                setNotes(prev => prev.filter((_, i) => i !== noteIndexToRemove))
                toast.success('Anotação removida com sucesso!')
            } else {
                toast.error('Erro ao remover anotação')
            }
        } catch (error: any) {
            console.error('Erro ao remover anotação:', error)
            toast.error('Erro ao remover anotação')
        } finally {
            setShowRemoveNoteModal(false)
            setNoteIndexToRemove(null)
        }
    }

    const loadMetadataTemplate = async () => {
        if (!user?.companyId) return

        try {
            const response = await getSettingByName(
                'client_metadata',
                user.companyId
            )
            if (response.success && response.result?.jsonValue) {
                const fields = response.result.jsonValue as MetadataField[]
                if (Array.isArray(fields) && fields.length > 0) {
                    setMetadata(
                        fields.map(field => ({
                            key: field.key || '',
                            value: '',
                            expanded: field.isTextarea || false,
                        }))
                    )
                }
            }
        } catch (error) {
            console.error('Erro ao carregar template de metadata:', error)
        }
    }

    const saveMetadataTemplate = async () => {
        if (!user?.companyId) {
            console.error('[saveMetadataTemplate] CompanyId não encontrado')
            return false
        }

        const fields: MetadataField[] = metadata
            .filter(item => item.key.trim())
            .map(item => ({
                key: item.key.trim(),
                isTextarea: item.expanded,
            }))

        if (fields.length === 0) {
            console.log('[saveMetadataTemplate] Nenhum campo para salvar')
            return false
        }

        try {
            console.log('[saveMetadataTemplate] Iniciando salvamento...', {
                name: 'client_metadata',
                companyId: user.companyId,
                fieldsCount: fields.length,
                fields,
            })

            const existingSetting = await getSettingByName(
                'client_metadata',
                user.companyId
            )

            console.log('[saveMetadataTemplate] Resultado da busca:', {
                success: existingSetting.success,
                hasResult: !!existingSetting.result,
                result: existingSetting.result,
            })

            if (existingSetting.success && existingSetting.result) {
                console.log(
                    '[saveMetadataTemplate] Atualizando setting existente...',
                    {
                        id: existingSetting.result.id,
                    }
                )
                const updateResponse = await updateSetting(
                    existingSetting.result.id,
                    {
                        jsonValue: fields,
                    }
                )
                console.log(
                    '[saveMetadataTemplate] Resposta da atualização:',
                    updateResponse
                )
                if (updateResponse.success) {
                    console.log(
                        '[saveMetadataTemplate] Setting atualizada com sucesso'
                    )
                    return true
                } else {
                    console.error(
                        '[saveMetadataTemplate] Erro ao atualizar:',
                        updateResponse.error
                    )
                    throw new Error(
                        updateResponse.error || 'Erro ao atualizar setting'
                    )
                }
            } else {
                console.log('[saveMetadataTemplate] Criando nova setting...')
                const createResponse = await createSetting({
                    name: 'client_metadata',
                    value: 'metadata_template',
                    companyId: user.companyId,
                    jsonValue: fields,
                })
                console.log(
                    '[saveMetadataTemplate] Resposta da criação:',
                    createResponse
                )
                if (createResponse.success) {
                    console.log(
                        '[saveMetadataTemplate] Setting criada com sucesso'
                    )
                    return true
                } else {
                    console.error(
                        '[saveMetadataTemplate] Erro ao criar:',
                        createResponse.error
                    )
                    throw new Error(
                        createResponse.error || 'Erro ao criar setting'
                    )
                }
            }
        } catch (error: any) {
            console.error('[saveMetadataTemplate] Erro capturado:', error)
            console.error('[saveMetadataTemplate] Stack:', error?.stack)
            throw error
        }
    }

    const formatCpf = (value: string): string => {
        const numbers = value.replace(/\D/g, '')
        if (numbers.length <= 3) return numbers
        if (numbers.length <= 6)
            return `${numbers.slice(0, 3)}.${numbers.slice(3)}`
        if (numbers.length <= 9)
            return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`
        return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`
    }

    const formatPhone = (value: string): string => {
        const numbers = value.replace(/\D/g, '').slice(0, 11)
        if (numbers.length <= 2) return numbers
        if (numbers.length <= 7)
            return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`
        if (numbers.length <= 10)
            return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`
    }

    const validateForm = (): boolean => {
        if (!formData.name.trim()) {
            toast.error('Nome é obrigatório')
            return false
        }
        const cpfNumbers = formData.cpf.replace(/\D/g, '')
        if (!cpfNumbers && isCpfRequired) {
            toast.error('CPF é obrigatório')
            return false
        }
        if (cpfNumbers && cpfNumbers.length !== 11) {
            toast.error('CPF deve ter 11 dígitos')
            return false
        }
        if (cpfNumbers && !isCpf(cpfNumbers)) {
            toast.error('CPF inválido')
            return false
        }
        if (!isEditing && formData.password && formData.password.length < 6) {
            toast.error('Senha deve ter no mínimo 6 caracteres')
            return false
        }
        if (!isEditing && formData.password && !formData.roleId) {
            toast.error(
                'Perfil de acesso é obrigatório quando uma senha é informada'
            )
            return false
        }
        return true
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) return
        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }

        setLoading(true)
        try {
            try {
                const templateSaved = await saveMetadataTemplate()
                if (templateSaved) {
                    console.log('Template de metadata salvo com sucesso')
                }
            } catch (templateError: any) {
                console.error(
                    'Erro ao salvar template (continuando com salvamento do cliente):',
                    templateError
                )
                toast.warning(
                    'Cliente salvo, mas houve um erro ao salvar o template de metadata'
                )
            }

            if (isEditing && id) {
                const metadataArray = metadata
                    .filter(item => item.key.trim() && item.value.trim())
                    .map(item => ({
                        key: item.key.trim(),
                        value: item.value.trim(),
                    }))

                const updateData: UpdateClientData = {
                    name: formData.name.trim() || undefined,
                    cpf: formData.cpf.replace(/\D/g, '') || null,
                    email: formData.email.trim() || undefined,
                    whatsAppNumber: formData.whatsAppNumber.trim() || undefined,
                    birthDate: formData.birthDate || undefined,
                    gender: formData.gender || undefined,
                    street: formData.street.trim() || undefined,
                    number: formData.number.trim() || undefined,
                    complement: formData.complement.trim() || undefined,
                    neighborhood: formData.neighborhood.trim() || undefined,
                    city: formData.city.trim() || undefined,
                    state: formData.state.trim() || undefined,
                    zipCode: formData.zipCode.trim() || undefined,
                    country: formData.country.trim() || undefined,
                    metadata:
                        metadataArray.length > 0 ? metadataArray : undefined,
                    partnerId: formData.partnerId,
                    professionalId: formData.professionalId,
                }

                const response = await updateClient(
                    parseInt(id, 10),
                    updateData
                )
                if (response.success) {
                    toast.success(`${ClientLabel} atualizado com sucesso!`)
                    navigate(`/person/clients/${id}`)
                } else {
                    toast.error(
                        response.error || `Erro ao atualizar ${clientLabel}`
                    )
                }
            } else {
                const metadataArray = metadata
                    .filter(item => item.key.trim() && item.value.trim())
                    .map(item => ({
                        key: item.key.trim(),
                        value: item.value.trim(),
                    }))

                const createData: CreateClientData = {
                    name: formData.name.trim(),
                    cpf: formData.cpf.replace(/\D/g, '') || undefined,
                    email: formData.email.trim() || undefined,
                    whatsAppNumber: formData.whatsAppNumber.trim() || undefined,
                    birthDate: formData.birthDate || undefined,
                    gender: formData.gender || undefined,
                    street: formData.street.trim() || undefined,
                    number: formData.number.trim() || undefined,
                    complement: formData.complement.trim() || undefined,
                    neighborhood: formData.neighborhood.trim() || undefined,
                    city: formData.city.trim() || undefined,
                    state: formData.state.trim() || undefined,
                    zipCode: formData.zipCode.trim() || undefined,
                    country: formData.country.trim() || 'BR',
                    organizationId: user.organizationId,
                    metadata:
                        metadataArray.length > 0 ? metadataArray : undefined,
                    password: formData.password || undefined,
                    roleId: formData.roleId
                        ? parseInt(formData.roleId, 10)
                        : undefined,
                    partnerId: formData.partnerId,
                    professionalId: formData.professionalId,
                }

                const response = await createClient(createData)
                if (response.success && response.result) {
                    toast.success(`${ClientLabel} criado com sucesso!`)
                    navigate(`/person/clients/${response.result.id}`)
                } else {
                    toast.error(
                        response.error || `Erro ao criar ${clientLabel}`
                    )
                }
            }
        } catch (error) {
            toast.error(`Erro ao salvar ${clientLabel}`)
        } finally {
            setLoading(false)
        }
    }

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando dados do {clientLabel}...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/person/clients')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-base-content">
                        {isEditing
                            ? `Editar ${ClientLabel}`
                            : `Novo ${ClientLabel}`}
                    </h1>
                    <p className="text-sm text-base-content/60">
                        {isEditing
                            ? `Atualize as informações do ${clientLabel}`
                            : `Preencha os dados do novo ${clientLabel}`}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="tabs tabs-boxed w-full !gap-7 bg-base-100">
                    <button
                        type="button"
                        onClick={() => setActiveTab('basic')}
                        className={`tab font-semibold transition-all ${
                            activeTab === 'basic'
                                ? 'tab-active bg-secondary text-primary-content shadow-lg scale-105'
                                : 'hover:bg-base-300'
                        }`}
                    >
                        Informações Básicas
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('metadata')}
                        className={`tab font-semibold transition-all ${
                            activeTab === 'metadata'
                                ? 'tab-active bg-secondary text-primary-content shadow-lg scale-105'
                                : 'hover:bg-base-300'
                        }`}
                    >
                        Informações Adicionais
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('attachments')}
                        className={`tab font-semibold transition-all ${
                            activeTab === 'attachments'
                                ? 'tab-active bg-secondary text-primary-content shadow-lg scale-105'
                                : 'hover:bg-base-300'
                        }`}
                    >
                        <MdAttachFile className="text-lg" />
                        Anexos
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('notes')}
                        className={`tab font-semibold transition-all ${
                            activeTab === 'notes'
                                ? 'tab-active bg-secondary text-primary-content shadow-lg scale-105'
                                : 'hover:bg-base-300'
                        }`}
                    >
                        <MdNote className="text-lg" />
                        Anotações
                    </button>
                </div>

                {activeTab === 'basic' && (
                    <>
                        <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                            <div className="card-body">
                                <h2 className="card-title text-lg mb-4">
                                    Informações Básicas
                                </h2>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Nome{' '}
                                            <span className="text-error">
                                                *
                                            </span>
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full focus:input-primary"
                                        placeholder={`Nome completo do ${clientLabel}`}
                                        value={formData.name}
                                        onChange={e =>
                                            updateFormField(
                                                'name',
                                                e.target.value
                                            )
                                        }
                                        required
                                    />
                                </div>

                                <div className="form-control w-full mt-4">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            CPF
                                            {isCpfRequired && (
                                                <span className="text-error">
                                                    {' '}*
                                                </span>
                                            )}
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full max-w-xs focus:input-primary"
                                        placeholder="000.000.000-00"
                                        maxLength={14}
                                        value={formData.cpf}
                                        onChange={e =>
                                            updateFormField(
                                                'cpf',
                                                formatCpf(e.target.value)
                                            )
                                        }
                                        required={isCpfRequired}
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Email
                                            </span>
                                        </label>
                                        <input
                                            type="email"
                                            className="input input-bordered w-full focus:input-primary"
                                            placeholder="email@exemplo.com"
                                            value={formData.email}
                                            onChange={e =>
                                                updateFormField(
                                                    'email',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                WhatsApp (Importante para
                                                lembretes e outros)
                                            </span>
                                        </label>
                                        <input
                                            type="tel"
                                            className="input input-bordered w-full focus:input-primary"
                                            placeholder="(00) 00000-0000"
                                            value={formatPhone(
                                                formData.whatsAppNumber
                                            )}
                                            onChange={e => {
                                                const raw = e.target.value
                                                    .replace(/\D/g, '')
                                                    .slice(0, 11)
                                                updateFormField(
                                                    'whatsAppNumber',
                                                    raw
                                                )
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Data de Nascimento
                                            </span>
                                        </label>
                                        <input
                                            type="date"
                                            className="input input-bordered w-full focus:input-primary"
                                            value={formData.birthDate}
                                            onChange={e =>
                                                updateFormField(
                                                    'birthDate',
                                                    e.target.value
                                                )
                                            }
                                            onPaste={e =>
                                                handleDateInputPaste(
                                                    e,
                                                    isoDate =>
                                                        updateFormField(
                                                            'birthDate',
                                                            isoDate
                                                        )
                                                )
                                            }
                                        />
                                    </div>
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Genero
                                            </span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full focus:select-primary"
                                            value={formData.gender}
                                            onChange={e =>
                                                updateFormField(
                                                    'gender',
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="">
                                                Selecione (opcional)
                                            </option>
                                            <option value="MALE">
                                                Masculino
                                            </option>
                                            <option value="FEMALE">
                                                Feminino
                                            </option>
                                            <option value="OTHER">Outro</option>
                                            <option value="PREFER_NOT_TO_SAY">
                                                Prefiro nao informar
                                            </option>
                                        </select>
                                    </div>
                                </div>

                                {/*!isEditing && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                        <div className="form-control w-full">
                                            <label className="label">
                                                <span className="label-text font-medium">
                                                    Senha de acesso
                                                </span>
                                            </label>
                                            <input
                                                type="password"
                                                className="input input-bordered w-full focus:input-primary"
                                                placeholder="Mínimo 6 caracteres"
                                                minLength={6}
                                                value={formData.password}
                                                onChange={e =>
                                                    updateFormField(
                                                        'password',
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            <label className="label">
                                                <span className="label-text-alt text-base-content/60">
                                                    Se informada, o {clientLabel} poderá acessar o sistema. Se o email for real, as credenciais serão enviadas por email.
                                                </span>
                                            </label>
                                        </div>

                                        <div className="form-control w-full">
                                            <label className="label">
                                                <span className="label-text font-medium">
                                                    Perfil de acesso
                                                </span>
                                            </label>
                                            <select
                                                className="select select-bordered w-full focus:select-primary"
                                                value={formData.roleId}
                                                onChange={e =>
                                                    updateFormField(
                                                        'roleId',
                                                        e.target.value
                                                    )
                                                }
                                                disabled={loadingRoles}
                                            >
                                                <option value="">
                                                    {loadingRoles
                                                        ? 'Carregando...'
                                                        : 'Selecionar perfil de acesso'}
                                                </option>
                                                {roles.map(role => (
                                                    <option
                                                        key={role.id}
                                                        value={String(role.id)}
                                                    >
                                                        {role.name}
                                                        {role.defaultForType === 'CLIENT'
                                                            ? ' (Padrão)'
                                                            : ''}
                                                    </option>
                                                ))}
                                            </select>

                                        </div>
                                    </div>
                                )*/}
                            </div>
                        </div>

                        <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                            <div className="card-body">
                                <h2 className="card-title text-lg mb-4">
                                    Endereço
                                </h2>

                                <div className="form-control w-full mb-4">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            CEP
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            className="input input-bordered w-full focus:input-primary"
                                            placeholder="01234-567"
                                            maxLength={9}
                                            value={formData.zipCode}
                                            onChange={e =>
                                                handleZipCodeChange(
                                                    e.target.value
                                                )
                                            }
                                        />
                                        {loadingCep && (
                                            <span className="loading loading-spinner loading-sm absolute right-3 top-1/2 -translate-y-1/2"></span>
                                        )}
                                    </div>
                                    <label className="label hidden md:block lg:block">
                                        <span className="label-text-alt text-base-content/60">
                                            Digite o CEP para buscar o endereço
                                            automaticamente
                                        </span>
                                    </label>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="form-control w-full md:col-span-2">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Rua
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full focus:input-primary"
                                            placeholder="Nome da rua"
                                            value={formData.street}
                                            onChange={e =>
                                                updateFormField(
                                                    'street',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Número
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full focus:input-primary"
                                            placeholder="123"
                                            value={formData.number}
                                            onChange={e =>
                                                updateFormField(
                                                    'number',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Complemento
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full focus:input-primary"
                                            placeholder="Apto, Bloco, etc."
                                            value={formData.complement}
                                            onChange={e =>
                                                updateFormField(
                                                    'complement',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Bairro
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full focus:input-primary"
                                            placeholder="Nome do bairro"
                                            value={formData.neighborhood}
                                            onChange={e =>
                                                updateFormField(
                                                    'neighborhood',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Cidade
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full focus:input-primary"
                                            placeholder="Cidade"
                                            value={formData.city}
                                            onChange={e =>
                                                updateFormField(
                                                    'city',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Estado (UF)
                                            </span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full focus:select-primary"
                                            value={formData.state}
                                            onChange={e =>
                                                updateFormField(
                                                    'state',
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="">Selecione</option>
                                            {[
                                                'AC',
                                                'AL',
                                                'AP',
                                                'AM',
                                                'BA',
                                                'CE',
                                                'DF',
                                                'ES',
                                                'GO',
                                                'MA',
                                                'MT',
                                                'MS',
                                                'MG',
                                                'PA',
                                                'PB',
                                                'PR',
                                                'PE',
                                                'PI',
                                                'RJ',
                                                'RN',
                                                'RS',
                                                'RO',
                                                'RR',
                                                'SC',
                                                'SP',
                                                'SE',
                                                'TO',
                                            ].map(uf => (
                                                <option key={uf} value={uf}>
                                                    {uf}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                            <div className="card-body">
                                <h2 className="card-title text-lg mb-4">
                                    <MdHandshake className="text-xl text-warning" />
                                    Vinculações (opcional)
                                </h2>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {!hidePartnerSelector && (
                                        <div className="form-control w-full">
                                            <PartnerSelector
                                                value={formData.partnerId}
                                                onChange={(
                                                    partnerId: number | null
                                                ) =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        partnerId,
                                                    }))
                                                }
                                                label="Parceiro Vinculado"
                                                placeholder="Selecionar parceiro..."
                                            />
                                        </div>
                                    )}

                                    {!hideProfessionalSelector && (
                                        <div className="form-control w-full">
                                            <ProfessionalSearchSelector
                                                value={formData.professionalId}
                                                onChange={id =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        professionalId: id,
                                                    }))
                                                }
                                                label="Profissional Responsável"
                                                placeholder="Selecionar profissional..."
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'metadata' && (
                    <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                        <div className="card-body">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="card-title text-lg">
                                    Informações Adicionais
                                </h2>
                                <button
                                    type="button"
                                    onClick={addMetadataItem}
                                    className="btn btn-sm btn-secondary gap-2"
                                >
                                    <MdAdd className="text-lg" />
                                    Adicionar
                                </button>
                            </div>

                            {metadata.length === 0 ? (
                                <div className="text-center py-8 text-base-content/60">
                                    <p>
                                        Nenhuma informação adicional cadastrada.
                                    </p>
                                    <p className="text-sm mt-2">
                                        Clique em "Adicionar" para incluir novas
                                        informações.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {metadata.map((item, index) => (
                                        <div
                                            key={index}
                                            className="bg-base-200/50 rounded-lg p-4 border border-base-300/50"
                                        >
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="form-control w-full">
                                                    <label className="label">
                                                        <span className="label-text font-medium">
                                                            Nome
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="input input-bordered w-full focus:input-primary"
                                                        placeholder="Ex: Observação, Nota, etc."
                                                        value={item.key}
                                                        onChange={e =>
                                                            updateMetadataItem(
                                                                index,
                                                                'key',
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>

                                                <div className="form-control w-full">
                                                    <label className="label">
                                                        <span className="label-text font-medium">
                                                            Preencha
                                                        </span>
                                                    </label>
                                                    <div className="relative">
                                                        {item.expanded ? (
                                                            <textarea
                                                                className="textarea textarea-bordered w-full focus:textarea-primary pr-10"
                                                                placeholder="Digite aqui..."
                                                                value={
                                                                    item.value
                                                                }
                                                                onChange={e =>
                                                                    updateMetadataItem(
                                                                        index,
                                                                        'value',
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                rows={4}
                                                            />
                                                        ) : (
                                                            <input
                                                                type="text"
                                                                className="input input-bordered w-full focus:input-primary pr-10"
                                                                placeholder="Digite aqui..."
                                                                value={
                                                                    item.value
                                                                }
                                                                onChange={e =>
                                                                    updateMetadataItem(
                                                                        index,
                                                                        'value',
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                toggleMetadataExpanded(
                                                                    index
                                                                )
                                                            }
                                                            className="absolute right-2 top-1/2 -translate-y-1/2 btn btn-ghost btn-sm btn-square"
                                                            title={
                                                                item.expanded
                                                                    ? 'Colapsar'
                                                                    : 'Expandir'
                                                            }
                                                        >
                                                            {item.expanded ? (
                                                                <MdExpandLess className="text-xl" />
                                                            ) : (
                                                                <MdExpandMore className="text-xl" />
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex justify-end mt-4">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        removeMetadataItem(
                                                            index
                                                        )
                                                    }
                                                    className="btn btn-sm btn-error btn-outline gap-2"
                                                >
                                                    <MdDelete className="text-lg" />
                                                    Remover
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'attachments' && (
                    <div className="space-y-4">
                        {!isEditing ? (
                            <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                                <div className="card-body">
                                    <div className="alert alert-info">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            className="stroke-current shrink-0 w-6 h-6"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                            ></path>
                                        </svg>
                                        <span>
                                            Salve os dados do cliente primeiro
                                            para anexar arquivos
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                                    <div className="card-body">
                                        <h2 className="card-title text-lg mb-4">
                                            Adicionar Anexo
                                        </h2>

                                        <div className="space-y-4">
                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text font-medium">
                                                        Arquivo
                                                        <span className="text-error ml-1">
                                                            *
                                                        </span>
                                                    </span>
                                                </label>
                                                <input
                                                    id="attachment-file"
                                                    type="file"
                                                    className="file-input file-input-bordered w-full focus:file-input-primary"
                                                    onChange={handleFileChange}
                                                    accept="*/*"
                                                />
                                                {uploadForm.file && (
                                                    <label className="label">
                                                        <span className="label-text-alt text-base-content/60">
                                                            Arquivo selecionado:{' '}
                                                            {
                                                                uploadForm.file
                                                                    .name
                                                            }
                                                        </span>
                                                    </label>
                                                )}
                                            </div>

                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text font-medium">
                                                        Descrição
                                                        <span className="text-error ml-1">
                                                            *
                                                        </span>
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="input input-bordered w-full focus:input-primary"
                                                    placeholder="Descrição do anexo"
                                                    value={
                                                        uploadForm.description
                                                    }
                                                    onChange={e =>
                                                        setUploadForm(prev => ({
                                                            ...prev,
                                                            description:
                                                                e.target.value,
                                                        }))
                                                    }
                                                    required
                                                />
                                            </div>

                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text font-medium">
                                                        Detalhes
                                                    </span>
                                                </label>
                                                <textarea
                                                    className="textarea textarea-bordered w-full focus:textarea-primary"
                                                    placeholder="Detalhes adicionais (opcional)"
                                                    value={uploadForm.details}
                                                    onChange={e =>
                                                        setUploadForm(prev => ({
                                                            ...prev,
                                                            details:
                                                                e.target.value,
                                                        }))
                                                    }
                                                    rows={3}
                                                />
                                            </div>

                                            <div className="flex justify-end">
                                                <button
                                                    type="button"
                                                    onClick={
                                                        handleUploadAttachment
                                                    }
                                                    className="btn btn-secondary gap-2"
                                                    disabled={
                                                        uploading ||
                                                        !uploadForm.file ||
                                                        !uploadForm.description.trim() ||
                                                        !user?.name
                                                    }
                                                >
                                                    {uploading ? (
                                                        <>
                                                            <span className="loading loading-spinner loading-sm"></span>
                                                            Enviando...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <MdAttachFile className="text-lg" />
                                                            Adicionar Anexo
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                                    <div className="card-body">
                                        <h2 className="card-title text-lg mb-4">
                                            Anexos ({attachments.length})
                                        </h2>

                                        {attachments.length === 0 ? (
                                            <div className="text-center py-8 text-base-content/60">
                                                <p>Nenhum anexo cadastrado.</p>
                                                <p className="text-sm mt-2">
                                                    Adicione um arquivo acima
                                                    para começar.
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                {attachments.map(
                                                    (attachment, index) => (
                                                        <div
                                                            key={index}
                                                            className="bg-base-200/50 rounded-lg p-4 border border-base-300/50"
                                                        >
                                                            <div className="flex items-start justify-between gap-4">
                                                                <div className="flex-1">
                                                                    <div className="flex items-center gap-2 mb-2">
                                                                        <MdAttachFile className="text-lg text-secondary" />
                                                                        <a
                                                                            href={
                                                                                attachment.filePath
                                                                            }
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="font-semibold text-secondary hover:underline"
                                                                        >
                                                                            {
                                                                                attachment.fileName
                                                                            }
                                                                        </a>
                                                                    </div>
                                                                    <div className="space-y-1 text-sm">
                                                                        <p>
                                                                            <span className="font-medium">
                                                                                Usuário:
                                                                            </span>{' '}
                                                                            {
                                                                                attachment.userName
                                                                            }
                                                                        </p>
                                                                        <p>
                                                                            <span className="font-medium">
                                                                                Descrição:
                                                                            </span>{' '}
                                                                            {
                                                                                attachment.description
                                                                            }
                                                                        </p>
                                                                        {attachment.details && (
                                                                            <p>
                                                                                <span className="font-medium">
                                                                                    Detalhes:
                                                                                </span>{' '}
                                                                                {
                                                                                    attachment.details
                                                                                }
                                                                            </p>
                                                                        )}
                                                                        {attachment.createdAt && (
                                                                            <p className="text-base-content/60">
                                                                                <span className="font-medium">
                                                                                    Data:
                                                                                </span>{' '}
                                                                                {new Date(
                                                                                    attachment.createdAt
                                                                                ).toLocaleString(
                                                                                    'pt-BR'
                                                                                )}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        handleRemoveAttachment(
                                                                            index
                                                                        )
                                                                    }
                                                                    className="btn btn-sm btn-error btn-outline gap-2"
                                                                >
                                                                    <MdDelete className="text-lg" />
                                                                    Remover
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'notes' && (
                    <div className="space-y-4">
                        {!isEditing ? (
                            <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                                <div className="card-body">
                                    <div className="alert alert-info">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            className="stroke-current shrink-0 w-6 h-6"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                            ></path>
                                        </svg>
                                        <span>
                                            Salve os dados do cliente primeiro
                                            para adicionar anotações
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                                    <div className="card-body">
                                        <h2 className="card-title text-lg mb-4">
                                            Adicionar Anotação
                                        </h2>

                                        <div className="form-control w-full">
                                            <label className="label">
                                                <span className="label-text font-medium">
                                                    Anotação
                                                    <span className="text-error ml-1">
                                                        *
                                                    </span>
                                                </span>
                                            </label>
                                            <textarea
                                                className="textarea textarea-bordered w-full focus:textarea-primary"
                                                placeholder="Digite sua anotação aqui..."
                                                value={noteText}
                                                onChange={e =>
                                                    setNoteText(e.target.value)
                                                }
                                                rows={5}
                                                required
                                            />
                                        </div>

                                        <div className="flex justify-end mt-4">
                                            <button
                                                type="button"
                                                onClick={handleAddNote}
                                                className="btn btn-secondary gap-2"
                                                disabled={
                                                    addingNote ||
                                                    !noteText.trim() ||
                                                    !user?.name
                                                }
                                            >
                                                {addingNote ? (
                                                    <>
                                                        <span className="loading loading-spinner loading-sm"></span>
                                                        Adicionando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <MdNote className="text-lg" />
                                                        Adicionar Anotação
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                                    <div className="card-body">
                                        <h2 className="card-title text-lg mb-4">
                                            Anotações ({notes.length})
                                        </h2>

                                        {notes.length === 0 ? (
                                            <div className="text-center py-8 text-base-content/60">
                                                <p>
                                                    Nenhuma anotação cadastrada.
                                                </p>
                                                <p className="text-sm mt-2">
                                                    Adicione uma anotação acima
                                                    para começar.
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                {notes.map((note, index) => (
                                                    <div
                                                        key={index}
                                                        className="bg-base-200/50 rounded-lg p-4 border border-base-300/50"
                                                    >
                                                        <div className="flex items-start justify-between gap-4">
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <MdNote className="text-lg text-secondary" />
                                                                    <span className="font-semibold">
                                                                        {
                                                                            note.userName
                                                                        }
                                                                    </span>
                                                                </div>
                                                                <p className="text-sm whitespace-pre-wrap">
                                                                    {note.note}
                                                                </p>
                                                                {note.createdAt && (
                                                                    <p className="text-xs text-base-content/60 mt-2">
                                                                        {new Date(
                                                                            note.createdAt
                                                                        ).toLocaleString(
                                                                            'pt-BR'
                                                                        )}
                                                                    </p>
                                                                )}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    handleRemoveNote(
                                                                        index
                                                                    )
                                                                }
                                                                className="btn btn-sm btn-error btn-outline gap-2"
                                                            >
                                                                <MdDelete className="text-lg" />
                                                                Remover
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {!(activeTab === 'attachments' && isEditing) &&
                    !(activeTab === 'notes' && isEditing) && (
                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => navigate('/person/clients')}
                                className="btn btn-ghost"
                                disabled={loading}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-secondary gap-2"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="loading loading-spinner loading-sm"></span>
                                        Salvando...
                                    </>
                                ) : (
                                    <>
                                        <MdSave className="text-lg" />
                                        Salvar
                                    </>
                                )}
                            </button>
                        </div>
                    )}
            </form>

            <ConfirmModal
                isOpen={showRemoveAttachmentModal}
                onClose={() => {
                    setShowRemoveAttachmentModal(false)
                    setAttachmentIndexToRemove(null)
                }}
                onConfirm={confirmRemoveAttachment}
                title="Remover anexo"
                message="Tem certeza que deseja remover este anexo?"
                confirmButtonText="Remover"
                cancelButtonText="Cancelar"
            />

            <ConfirmModal
                isOpen={showRemoveNoteModal}
                onClose={() => {
                    setShowRemoveNoteModal(false)
                    setNoteIndexToRemove(null)
                }}
                onConfirm={confirmRemoveNote}
                title="Remover anotação"
                message="Tem certeza que deseja remover esta anotação?"
                confirmButtonText="Remover"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
